import { breakpoints, type Breakpoint } from '../hooks/useBreakpoints';

/**
 * Responsive utility functions for detecting device capabilities and screen sizes
 */

/**
 * Check if the device is a mobile device based on user agent
 * Note: This is not always reliable. Use breakpoints for layout decisions.
 *
 * @returns true if the device is likely a mobile device
 */
export const isMobileDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;

  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

/**
 * Check if the device supports touch events
 *
 * @returns true if the device has touch capability
 */
export const isTouchDevice = (): boolean => {
  if (typeof window === 'undefined') return false;

  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-ignore - for older browsers
    (navigator.msMaxTouchPoints && navigator.msMaxTouchPoints > 0)
  );
};

/**
 * Check if the device is likely an iOS device
 *
 * @returns true if the device is iOS
 */
export const isIOS = (): boolean => {
  if (typeof navigator === 'undefined') return false;

  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPad on iOS 13+ detection
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
};

/**
 * Check if the device is likely an Android device
 *
 * @returns true if the device is Android
 */
export const isAndroid = (): boolean => {
  if (typeof navigator === 'undefined') return false;

  return /Android/.test(navigator.userAgent);
};

/**
 * Get the current breakpoint based on window width
 *
 * @param width Window width in pixels (defaults to window.innerWidth)
 * @returns Current breakpoint name
 */
export const getBreakpoint = (width?: number): Breakpoint => {
  const w = width ?? (typeof window !== 'undefined' ? window.innerWidth : 0);

  if (w >= breakpoints['2xl']) return '2xl';
  if (w >= breakpoints.xl) return 'xl';
  if (w >= breakpoints.lg) return 'lg';
  if (w >= breakpoints.md) return 'md';
  if (w >= breakpoints.sm) return 'sm';
  return 'xs';
};

/**
 * Get the current viewport width
 *
 * @returns Viewport width in pixels
 */
export const getViewportWidth = (): number => {
  if (typeof window === 'undefined') return 0;
  return window.innerWidth;
};

/**
 * Get the current viewport height
 *
 * @returns Viewport height in pixels
 */
export const getViewportHeight = (): number => {
  if (typeof window === 'undefined') return 0;
  return window.innerHeight;
};

/**
 * iOS Safe Area Constants
 * These represent the typical safe area insets for iOS devices
 */
export const iosSafeArea = {
  /** Top safe area for notch (iPhone X and newer) */
  top: {
    portrait: 44, // Status bar + notch
    landscape: 0, // No notch in landscape on most devices
  },
  /** Bottom safe area for home indicator */
  bottom: {
    portrait: 34, // Home indicator
    landscape: 21, // Home indicator in landscape
  },
  /** Side safe areas in landscape (for notch) */
  sides: {
    left: 44,
    right: 44,
  },
} as const;

/**
 * Minimum touch target size (Apple Human Interface Guidelines)
 * https://developer.apple.com/design/human-interface-guidelines/ios/visual-design/adaptivity-and-layout/
 */
export const MIN_TOUCH_TARGET = 44; // pixels

/**
 * Recommended touch target size for comfortable tapping
 */
export const COMFORTABLE_TOUCH_TARGET = 48; // pixels

/**
 * Check if the current viewport width is at or above a breakpoint
 *
 * @param breakpoint Target breakpoint
 * @param width Custom width to check (defaults to window.innerWidth)
 * @returns true if viewport is at or above the breakpoint
 */
export const isBreakpoint = (breakpoint: Breakpoint, width?: number): boolean => {
  const w = width ?? getViewportWidth();
  return w >= breakpoints[breakpoint];
};

/**
 * Check if viewport is in portrait orientation
 *
 * @returns true if portrait (height > width)
 */
export const isPortrait = (): boolean => {
  if (typeof window === 'undefined') return true;
  return window.innerHeight > window.innerWidth;
};

/**
 * Check if viewport is in landscape orientation
 *
 * @returns true if landscape (width > height)
 */
export const isLandscape = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth > window.innerHeight;
};

/**
 * Prevent default touch behavior (like pull-to-refresh)
 * Useful for interactive components that need touch gestures
 *
 * @param element Element to prevent default touch on
 */
export const preventTouchDefault = (element: HTMLElement): (() => void) => {
  const handler = (e: TouchEvent) => {
    if (e.touches.length > 1) return; // Allow multi-touch
    e.preventDefault();
  };

  element.addEventListener('touchstart', handler, { passive: false });

  // Return cleanup function
  return () => {
    element.removeEventListener('touchstart', handler);
  };
};

/**
 * Enable/disable body scroll (useful for modals)
 *
 * @param disable true to disable scroll, false to enable
 */
export const setBodyScroll = (disable: boolean): void => {
  if (typeof document === 'undefined') return;

  if (disable) {
    // Store current scroll position
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
  } else {
    // Restore scroll position
    const scrollY = document.body.style.top;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, parseInt(scrollY || '0') * -1);
  }
};

/**
 * Trigger haptic feedback on supported devices (iOS)
 * Requires iOS 13+ and user gesture
 *
 * @param type Type of haptic feedback
 */
export const triggerHaptic = (
  type: 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error' = 'light'
): void => {
  // @ts-ignore - Haptic feedback is not in TypeScript definitions
  if (typeof window !== 'undefined' && window.navigator?.vibrate) {
    // Vibration API (Android)
    const patterns = {
      light: 10,
      medium: 20,
      heavy: 30,
      success: [10, 50, 10],
      warning: [20, 100, 20],
      error: [30, 100, 30, 100, 30],
    };

    window.navigator.vibrate(patterns[type]);
  }

  // Note: iOS haptic feedback requires native implementation
  // through WKWebView or Capacitor/Cordova plugins
};

/**
 * Get safe area inset values for iOS
 * Uses CSS env() variables if available
 *
 * @returns Object with safe area insets
 */
export const getSafeAreaInsets = (): {
  top: number;
  bottom: number;
  left: number;
  right: number;
} => {
  if (typeof window === 'undefined' || !isIOS()) {
    return { top: 0, bottom: 0, left: 0, right: 0 };
  }

  // Try to get CSS env() values
  const style = getComputedStyle(document.documentElement);

  const top = parseInt(style.getPropertyValue('env(safe-area-inset-top)')) || 0;
  const bottom = parseInt(style.getPropertyValue('env(safe-area-inset-bottom)')) || 0;
  const left = parseInt(style.getPropertyValue('env(safe-area-inset-left)')) || 0;
  const right = parseInt(style.getPropertyValue('env(safe-area-inset-right)')) || 0;

  return { top, bottom, left, right };
};

/**
 * Clamp a value between min and max
 *
 * @param value Value to clamp
 * @param min Minimum value
 * @param max Maximum value
 * @returns Clamped value
 */
export const clamp = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

/**
 * Linear interpolation between two values
 *
 * @param start Start value
 * @param end End value
 * @param t Progress (0-1)
 * @returns Interpolated value
 */
export const lerp = (start: number, end: number, t: number): number => {
  return start + (end - start) * t;
};

/**
 * Map a value from one range to another
 *
 * @param value Value to map
 * @param inMin Input range minimum
 * @param inMax Input range maximum
 * @param outMin Output range minimum
 * @param outMax Output range maximum
 * @returns Mapped value
 */
export const mapRange = (
  value: number,
  inMin: number,
  inMax: number,
  outMin: number,
  outMax: number
): number => {
  return ((value - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin;
};

/**
 * Format bytes to human-readable string
 * Useful for displaying file sizes on mobile
 *
 * @param bytes Number of bytes
 * @param decimals Number of decimal places
 * @returns Formatted string (e.g., "1.5 MB")
 */
export const formatBytes = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * Debounce a function
 * Useful for resize handlers and scroll listeners
 *
 * @param fn Function to debounce
 * @param delay Delay in milliseconds
 * @returns Debounced function
 */
export const debounce = <T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

/**
 * Throttle a function
 * Useful for scroll and touch move handlers
 *
 * @param fn Function to throttle
 * @param limit Time limit in milliseconds
 * @returns Throttled function
 */
export const throttle = <T extends (...args: any[]) => any>(
  fn: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      fn(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};
